import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../../hooks/use-disclosure";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import { PHYSICAL_ASSESSMENT_EXAM_SECTIONS } from "../components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";
import MentalStatusExaminationModal from "../components/form-components/InitialPsychotherapy/modals/MentalStatusExamination";
import RiskAssessmentModal from "../components/form-components/InitialPsychotherapy/modals/RiskAssessmentModal";
import {
  RiskAssessmentSummary,
  SelectedSectionsSummary,
} from "../components/form-components/InitialPsychotherapy/SelectedFormSummary";
import {
  BIRP_PATIENT_PARTICIPATION_FIELDS,
  BIRP_PATIENT_PARTICIPATION_SECTIONS,
  BIRP_PROGRESS_OPTIONS,
  BIRP_RESPONSE_TO_INTERVENTION_FIELDS,
  BIRP_RESPONSE_TO_INTERVENTION_SECTIONS,
  BIRP_THERAPEUTIC_INTERVENTIONS_FIELDS,
  BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS,
  BIRP_TOPICS_ADDRESSED_FIELDS,
  BIRP_TOPICS_ADDRESSED_SECTIONS,
} from "../components/form-components/birp-note/BirpNoteConstant";
import BirpCheckboxModal from "../components/form-components/birp-note/modals/BirpCheckboxModal";
import ProgressModal from "../components/form-components/birp-note/modals/ProgressModal";
import PrimaryCareSoapNotePlan from "../components/form-components/primary-note/PrimaryCareSoapNotePlan";

interface BirpNoteProps {
  formik: FormikProps<Form2Payload>;
}

/**
 * BIRP (Behavior, Intervention, Response, Plan) Progress Note.
 *
 * The MSE, Risk Assessment, ICD picker, the whole Plan section and the Follow Up
 * & Instructions section are the same components — and the same formik keys —
 * the Psychotherapy and Psychiatric Notes use, so the notes stay in sync. The
 * four Intervention/Response multiselects and the single-select Progress are
 * BIRP specific and follow the same add-button → modal → summary flow.
 */
const BirpNote = ({ formik }: BirpNoteProps) => {
  const mseDisclosure = useDisclosure();
  const riskAssessmentDisclosure = useDisclosure();
  const icdDisclosure = useDisclosure();
  const therapeuticInterventionsDisclosure = useDisclosure();
  const topicsAddressedDisclosure = useDisclosure();
  const patientParticipationDisclosure = useDisclosure();
  const responseToInterventionDisclosure = useDisclosure();
  const progressDisclosure = useDisclosure();
  const appointmentDisclosure = useDisclosure();
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );

  const progressLabel = BIRP_PROGRESS_OPTIONS.find(
    (option) => option.value === formik.values?.progress
  )?.label;

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Chief Complaint is captured once by the encounter header above the
            form, so the note body does not repeat it. */}

        {/* ═══════════════ BEHAVIOR ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Behavior</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            rows={4}
            name="behaviorFreeNote"
            placeholder="Behavior..."
            onBlur={formik.handleBlur}
            value={formik.values?.behaviorFreeNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Behavior → Mental Status Examination (MSE) */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Mental Status Examination (MSE)</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => mseDisclosure.open()}
            label="Add Mental Status Examination (MSE)"
          />
          <MentalStatusExaminationModal
            disclosureHost={mseDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PHYSICAL_ASSESSMENT_EXAM_SECTIONS}
            overallNoteKey="overallMseNote"
            overallNoteLabel="Overall MSE Note"
          />
        </Grid>

        {/* Behavior → Risk Assessment */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Risk Assessment:</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => riskAssessmentDisclosure.open()}
            label="Add Risk Assessment"
          />
          <RiskAssessmentModal
            disclosureHost={riskAssessmentDisclosure}
            formik={formik}
          />
          <RiskAssessmentSummary formik={formik} />
        </Grid>

        {/* ═══════════════ INTERVENTION ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Intervention</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            rows={4}
            name="interventionFreeNote"
            placeholder="Intervention..."
            onBlur={formik.handleBlur}
            value={formik.values?.interventionFreeNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Intervention → Therapeutic Interventions */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Therapeutic Interventions</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => therapeuticInterventionsDisclosure.open()}
            label="Add Therapeutic Interventions"
          />
          <BirpCheckboxModal
            disclosureHost={therapeuticInterventionsDisclosure}
            formik={formik}
            title="Therapeutic Interventions"
            sections={BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS}
            fields={BIRP_THERAPEUTIC_INTERVENTIONS_FIELDS}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS}
            showSectionLabel={false}
          />
        </Grid>

        {/* Intervention → Topics Addressed */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Topics Addressed</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => topicsAddressedDisclosure.open()}
            label="Add Topics Addressed"
          />
          <BirpCheckboxModal
            disclosureHost={topicsAddressedDisclosure}
            formik={formik}
            title="Topics Addressed"
            sections={BIRP_TOPICS_ADDRESSED_SECTIONS}
            fields={BIRP_TOPICS_ADDRESSED_FIELDS}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={BIRP_TOPICS_ADDRESSED_SECTIONS}
            showSectionLabel={false}
          />
        </Grid>

        {/* Intervention → ICD Codes. Same picker and same `assessmentCodes`
            key every other note type writes to. */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>ICD Codes</CustomTitle>
          <StyleAddButton
            w="120px"
            onClick={() => icdDisclosure.open()}
            label="Add ICD Codes"
          />
          <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
          {!!formik.values?.assessmentCodes?.length && (
            <Flex flexDirection={"column"} width={"100%"} gap={1} mt={1}>
              {renderSelectedIcdCodes(
                formik.values?.assessmentCodes ?? [],
                (code) => {
                  formik.setFieldValue("assessmentCodes", [
                    ...(formik.values?.assessmentCodes ?? []).filter(
                      (it) => it.value !== code.value
                    ),
                  ]);
                },
                "20vh"
              )}
            </Flex>
          )}
        </Grid>

        {/* ═══════════════ RESPONSE ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Response</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            rows={4}
            name="responseFreeNote"
            placeholder="Response..."
            onBlur={formik.handleBlur}
            value={formik.values?.responseFreeNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Response → Patient Participation */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Patient Participation</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => patientParticipationDisclosure.open()}
            label="Add Patient Participation"
          />
          <BirpCheckboxModal
            disclosureHost={patientParticipationDisclosure}
            formik={formik}
            title="Patient Participation"
            sections={BIRP_PATIENT_PARTICIPATION_SECTIONS}
            fields={BIRP_PATIENT_PARTICIPATION_FIELDS}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={BIRP_PATIENT_PARTICIPATION_SECTIONS}
            showSectionLabel={false}
          />
        </Grid>

        {/* Response → Response to Intervention */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Response to Intervention</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => responseToInterventionDisclosure.open()}
            label="Add Response to Intervention"
          />
          <BirpCheckboxModal
            disclosureHost={responseToInterventionDisclosure}
            formik={formik}
            title="Response to Intervention"
            sections={BIRP_RESPONSE_TO_INTERVENTION_SECTIONS}
            fields={BIRP_RESPONSE_TO_INTERVENTION_FIELDS}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={BIRP_RESPONSE_TO_INTERVENTION_SECTIONS}
            showSectionLabel={false}
          />
        </Grid>

        {/* Response → Progress (single select) */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Progress</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => progressDisclosure.open()}
            label="Add Progress"
          />
          <ProgressModal disclosureHost={progressDisclosure} formik={formik} />
          {!!progressLabel && (
            <Flex flexDirection="column" width="100%" mt={0.5}>
              <Text fontSize="14px">
                <Text component="span" fontSize="14px" fontWeight="bold">
                  Progress:{" "}
                </Text>
                {progressLabel}
              </Text>
            </Flex>
          )}
        </Grid>

        {/* ═══════════════ PLAN ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Plan
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Plan Note"
            rows={3}
            name="planFreeNote"
            placeholder="Plan..."
            onBlur={formik.handleBlur}
            value={formik.values?.planFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <PrimaryCareSoapNotePlan
            formik={formik}
            showSendAssessment
            showAssignEducation
            showTherapies
          />
        </Grid>

        {/* ═══════════════ FOLLOW UP & INSTRUCTIONS ═══════════════
            Same `followUp` key, placeholder and Schedule F/U Appointment flow as
            the Psychiatric Note, so both notes save and print identically. */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Follow Up & Instructions
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            rows={4}
            variant="outlined"
            name="followUp"
            placeholder="Follow Up & Instructions..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUp || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        {hasUpdateAppointment && (
          <Grid size={12}>
            <StyleAddButton
              w="120px"
              onClick={() => {
                appointmentDisclosure.open();
              }}
              label="Schedule F/U Appointment"
            />
            {appointmentDisclosure.opened && (
              <NewAppointment
                enablePrefillPatientData
                opened={appointmentDisclosure.opened}
                onClose={appointmentDisclosure.close}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default BirpNote;
